/**
 * W2B — Client Action Runs Panel.
 *
 * Read-only inspection of workflow action_runs queued for this client.
 * No execute / retry / cancel buttons here — W2C will add those.
 */
import { useCallback, useEffect, useState } from "react";
import { Clock, Zap, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const ACTION_TYPE_LABELS = {
    send_sms: "Send SMS to Client",
    send_email: "Send Email to Client",
    notify_user: "Notify User",
    add_internal_note: "Add Internal Note",
    change_workflow_status: "Change Workflow Status",
};

const NOTIFY_TARGET_LABELS = {
    processor: "Processor",
    sales_rep: "Sales Rep",
    specific_user: "Specific User",
    admins: "All Admins",
};

const STATUS_BADGE_CLASS = {
    queued: "bg-amber-100 text-amber-800 border border-amber-200",
    scheduled: "bg-sky-100 text-sky-800 border border-sky-200",
    running: "bg-indigo-100 text-indigo-800 border border-indigo-200",
    completed: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    failed: "bg-rose-100 text-rose-800 border border-rose-200",
    skipped: "bg-slate-100 text-slate-700 border border-slate-200",
    cancelled: "bg-slate-100 text-slate-700 border border-slate-200",
};

function formatScheduledLocal(r) {
    if (!r.scheduled_for) return null;
    const parts = [];
    if (r.delay_count !== undefined && r.delay_count !== null && r.delay_unit === "business_days") {
        if (r.delay_count === 0) parts.push(`Today at ${r.run_time || r.scheduled_for_local_time}`);
        else parts.push(`After ${r.delay_count} business day${r.delay_count === 1 ? "" : "s"} at ${r.run_time || r.scheduled_for_local_time}`);
    }
    return parts.join(" ");
}

function summarizeRun(r) {
    switch (r.action_type) {
        case "send_sms": {
            const tpl = r.sms_template_name || r.sms_template_id;
            const route = r.provider_route_name || r.metadata?.snapshot_provider_route_name;
            const msid = r.metadata?.snapshot_twilio_messaging_service_sid;
            const masked = msid && msid.length > 6
                ? `${msid.slice(0, 2)}${"•".repeat(msid.length - 6)}${msid.slice(-4)}`
                : msid;
            const pmid = r.metadata?.provider_message_id;
            const parts = [];
            if (tpl) parts.push(`Template: ${tpl}`);
            if (route) parts.push(`Route: ${route}`);
            if (masked) parts.push(`MSID: ${masked}`);
            if (pmid) parts.push(`Provider msg: ${pmid}`);
            if (r.metadata?.resolved_client_phone_present === false) parts.push("no client phone");
            return parts.join(" · ");
        }
        case "send_email": {
            const tpl = r.email_template_name || r.email_template_id;
            const mode = r.metadata?.snapshot_email_send_mode;
            const modeLabel = mode === "dynamic_template" ? "SendGrid Dynamic" : "Local CaseFlow";
            const route = r.provider_route_name || r.metadata?.snapshot_provider_route_name;
            const sgid = r.metadata?.snapshot_sendgrid_template_id;
            const pmid = r.metadata?.provider_message_id;
            const parts = [];
            if (tpl) parts.push(`Template: ${tpl}`);
            if (mode) parts.push(`Mode: ${modeLabel}`);
            if (sgid) parts.push(`SG: ${sgid.slice(0, 10)}…`);
            if (route) parts.push(`Route: ${route}`);
            if (pmid) parts.push(`Provider msg: ${pmid}`);
            if (r.metadata?.resolved_client_email_present === false) parts.push("no client email");
            return parts.join(" · ");
        }
        case "notify_user": {
            if (r.notification_target === "specific_user") {
                return `Notify ${r.target_user_name || r.target_user_id || "…"}`;
            }
            const t = NOTIFY_TARGET_LABELS[r.notification_target] || r.notification_target || "…";
            const resolved = r.metadata?.resolved_target_user_name;
            return resolved ? `Notify ${t} — ${resolved}` : `Notify ${t}`;
        }
        case "add_internal_note": {
            const body = r.note_body || "";
            return `“${body.slice(0, 60)}${body.length > 60 ? "…" : ""}”`;
        }
        case "change_workflow_status":
            return `→ ${r.target_workflow_status_name || r.target_workflow_status_id || "…"}`;
        default:
            return "";
    }
}

export default function ClientActionRunsPanel({ clientId }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const r = await api.get(`/clients/${clientId}/action-runs`);
            setRuns(r.data || []);
        } catch { /* empty */ }
        finally { setLoading(false); }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-testid="client-action-runs-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-600" /> Workflow Action Queue
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Notify + Add-Note actions execute automatically. External SMS/Email sends land in W2C-2.
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="action-runs-refresh">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>
            {loading ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : runs.length === 0 ? (
                <div className="text-xs text-muted-foreground italic border border-dashed border-slate-200 rounded px-3 py-2">
                    No action runs queued for this client yet.
                </div>
            ) : (
                <ul className="space-y-2">
                    {runs.map(r => {
                        const tsLabel = r.status === "completed" ? r.completed_at
                            : r.status === "failed" ? r.failed_at
                            : r.status === "skipped" ? r.skipped_at
                            : r.status === "scheduled" ? r.scheduled_for
                            : r.queued_at;
                        const tsPrefix = r.status === "completed" ? "Completed"
                            : r.status === "failed" ? "Failed"
                            : r.status === "skipped" ? "Skipped"
                            : r.status === "scheduled" ? "Scheduled for"
                            : "Queued";
                        const scheduleLabel = r.status === "scheduled" ? formatScheduledLocal(r) : null;
                        const onlyIfStill = r.metadata?.only_if_still_in_status;
                        const notifCount = (r.metadata?.created_notification_ids || []).length;
                        const noteId = r.metadata?.created_note_id;
                        return (
                            <li
                                key={r.id}
                                className="rounded-lg border border-border bg-white px-3 py-2 flex flex-wrap items-center gap-3"
                                data-testid={`action-run-${r.id}`}
                            >
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${STATUS_BADGE_CLASS[r.status] || STATUS_BADGE_CLASS.queued}`}
                                    data-testid={`action-run-status-${r.id}`}
                                >
                                    {r.status}
                                </span>
                                <div className="flex-1 min-w-[240px]">
                                    <div className="text-sm font-medium text-gray-900">
                                        {ACTION_TYPE_LABELS[r.action_type] || r.action_type}
                                        {r.action_title ? <span className="text-muted-foreground font-normal"> — {r.action_title}</span> : null}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{summarizeRun(r)}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {tsPrefix} {new Date(tsLabel || r.queued_at).toLocaleString()} · trigger: {r.to_workflow_status_name || r.workflow_status_name}
                                    </div>
                                    {scheduleLabel && (
                                        <div className="text-[11px] text-sky-800 mt-0.5" data-testid={`action-run-schedule-${r.id}`}>
                                            {scheduleLabel}
                                            {r.scheduled_for_timezone ? ` (${r.scheduled_for_timezone})` : ""}
                                            {onlyIfStill ? " · may skip if client leaves this status" : ""}
                                        </div>
                                    )}
                                    {r.status_reason && (
                                        <div className={`text-[11px] mt-0.5 ${r.status === "failed" ? "text-rose-700" : r.status === "skipped" ? "text-amber-700" : "text-emerald-700"}`}>
                                            {r.status_reason}
                                        </div>
                                    )}
                                    {(notifCount > 0 || noteId) && (
                                        <div className="text-[11px] text-emerald-700 mt-0.5">
                                            {notifCount > 0 && <span>✓ {notifCount} notification{notifCount === 1 ? "" : "s"} delivered </span>}
                                            {noteId && <span>✓ Note created</span>}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
